import { Component, computed, effect, forwardRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
	AbstractControl,
	ControlValueAccessor,
	FormBuilder,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
	ValidationErrors,
	Validator,
	Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslocoPipe } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { ENVIRONMENT } from '../../../../../../../environments/environment.interface';
import { CreateCareerStatusDynamicFields } from '../../../../../../api/app-types/career.app';
import { County } from '../../../../../../api/app-types/county.app';
import { InstitutionBranch, InstitutionPerson } from '../../../../../../api/app-types/institution.app';
import { SearchResultItem } from '../../../../../../api/app-types/search.app';
import { SocialSecurityStatusActivity } from '../../../../../../api/app-types/social-security-status.app';
import { CATEGORIES_API } from '../../../../../../api/injection-tokens/services.token';
import {
	INSTITUTION_PERSON_SERVICE,
	provideInstitutionPersonService,
} from '../../../../../../api/services/institution-person.service';
import { INSTITUTION_SERVICE, provideInstitutionApi } from '../../../../../../api/services/institution.service';
import { provideSearchApi, SEARCH_API } from '../../../../../../api/services/search.service';
import { LvrField } from '../../../../../../core/components/field/field';
import { LvrSearchableSelect } from '../../../../../../core/components/searchable-select/searchable-select';
import { LvrSectionForm } from '../../../../../../core/components/section/section-form';
import { LvrInputDirective } from '../../../../../../core/directives/lvr-input.directive';
import { hasError } from '../../../../../../shared/helpers/has-error.func';
import { LvrPaddingDirective } from '../../../../../../shared/padding/padding.directive';
import { ImmediateErrorStateMatcher } from '../../../../../../shared/validators/immediate-error-state-matcher';
import { Subject } from 'rxjs';

@Component({
	selector: 'lvr-social-security-status-full',
	templateUrl: './social-security-status-full.html',
	styleUrl: './social-security-status-full.scss',
	imports: [
		MatCheckbox,
		LvrInputDirective,
		TranslocoPipe,
		MatInput,
		MatFormFieldModule,
		LvrField,
		ReactiveFormsModule,
		LvrPaddingDirective,
		LvrSearchableSelect,
		MatAutocompleteModule,
		LvrSectionForm,
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SocialSecurityStatusFull),
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => SocialSecurityStatusFull),
			multi: true,
		},
		provideSearchApi,
		provideInstitutionApi,
		provideInstitutionPersonService,
	],
})
export class SocialSecurityStatusFull implements ControlValueAccessor, Validator {
	private readonly searchApi = inject(SEARCH_API);
	private readonly institutionApi = inject(INSTITUTION_SERVICE);
	private readonly institutionPersonApi = inject(INSTITUTION_PERSON_SERVICE);
	private readonly categoriesApi = inject(CATEGORIES_API);
	public readonly isLoading = signal(false);
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly env = inject(ENVIRONMENT);
	public readonly institutionSearchResults = signal<SearchResultItem[]>([]);
	// defines if it status "J" or "j" which is different between branch linking is required (J) or
	// optional (j).
	public readonly statusType = input.required<SocialSecurityStatusActivity>();
	public readonly isSocialSecurityStatusActivityJ = computed(
		() => this.statusType() === SocialSecurityStatusActivity.J,
	);
	public readonly cityDistricts = signal<County[]>([]);

	// Infinite scroll pagination
	private institutionSearchTerm = '';
	private readonly institutionSearch = new Subject<string>();
	public searchInstitutions(searchTerm: string): void {
		this.institutionSearchTerm = searchTerm;
		this.institutionSearch.next(searchTerm);
	}
	private institutionOffset = 0;
	private readonly pageSize = 10;
	private readonly hasMoreInstitutionResults = signal(false);
	public readonly totalInstitutionResults = signal(0);

	private readonly fb = inject(FormBuilder);
	public readonly form = this.fb.group({
		typeAndScopeOfTheWorkplace: [
			'',
			[Validators.maxLength(this.env.formConfiguration.career.typeAndScopeOfTheWorkplaceLength)],
		],
		remark: ['', [Validators.maxLength(this.env.formConfiguration.career.remarkLength)]],
		institutionInformed: [false, []],
		jobTitle: ['', [Validators.maxLength(this.env.formConfiguration.career.jobTitleLength)]],
		branchId: [
			{
				value: undefined,
				disabled: true,
			},
		] as unknown as number | null,
		personId: [
			{
				value: undefined,
				disabled: true,
			},
		],
		institution: this.fb.control<SearchResultItem | string>(''),
		cityDistrictId: [
			{
				value: undefined,
				disabled: true,
			},
		] as unknown as number | null,
	});
	private readonly formValueChanges = toSignal(this.form.valueChanges, { initialValue: this.form.value });
	public readonly branches = signal<InstitutionBranch[]>([]);
	public readonly persons = signal<InstitutionPerson[]>([]);
	public readonly branchLabel = (branch: InstitutionBranch) => branch.name ?? '';
	public readonly personLabel = (person: InstitutionPerson) => `${person.firstName} ${person.name}`;
	public readonly institutionValue = (institution: SearchResultItem): SearchResultItem => institution;
	public readonly institutionLabel = (institution: SearchResultItem): string => institution.item.name ?? '';
	private previousInstitutionId: number | undefined;

	// Sadly we need to use "null" here as "undefined" does not work with ControlValueAccessor
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onChange = (_value: CreateCareerStatusDynamicFields | null) => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onTouched = () => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onValidationChange = () => {};

	// Sadly we need to use "null" here as "undefined" does not work with ControlValueAccessor
	private readonly value = signal<CreateCareerStatusDynamicFields | null>(null);
	private readonly disabled = signal(false);

	public constructor() {
		effect(() => {
			const isRequired = this.statusType() === SocialSecurityStatusActivity.J;
			const branchIdCtrl = this.form.get('branchId');
			const jobTitleCtrl = this.form.get('jobTitle');
			const typeCtrl = this.form.get('typeAndScopeOfTheWorkplace');
			const cityDistrictCtrl = this.form.get('cityDistrictId');
			const institutionCtrl = this.form.get('institution');

			if (isRequired) {
				institutionCtrl?.addValidators(Validators.required);
				branchIdCtrl?.addValidators(Validators.required);
				jobTitleCtrl?.addValidators(Validators.required);
				typeCtrl?.addValidators(Validators.required);
				cityDistrictCtrl?.addValidators(Validators.required);
			} else {
				institutionCtrl?.removeValidators(Validators.required);
				branchIdCtrl?.removeValidators(Validators.required);
				jobTitleCtrl?.removeValidators(Validators.required);
				typeCtrl?.removeValidators(Validators.required);
				cityDistrictCtrl?.removeValidators(Validators.required);
			}

			institutionCtrl?.updateValueAndValidity({ emitEvent: false });
			branchIdCtrl?.updateValueAndValidity({ emitEvent: false });
			jobTitleCtrl?.updateValueAndValidity({ emitEvent: false });
			typeCtrl?.updateValueAndValidity({ emitEvent: false });
			cityDistrictCtrl?.updateValueAndValidity({ emitEvent: false });
		});
		effect(() => {
			const value = this.formValueChanges() as CreateCareerStatusDynamicFields;
			this.value.set(value);
			this._onChange(value);
		});
		this.institutionSearch
			.pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
			.subscribe((searchTerm) => {
				this.updateInstitutionResults(searchTerm);
			});

		// React only to the selected institution
		this.form.controls.institution.valueChanges
			.pipe(
				distinctUntilChanged(),
				map((value) => value ?? ''),
				takeUntilDestroyed(),
			)
			.subscribe((searchedInstitution) => {
				this.updateInstitutionDependents(searchedInstitution);
			});

		// Trigger validation updates when form status changes
		effect(() => {
			this.formValueChanges();
			this._onValidationChange();
		});
		//Autoselect Branch, if is Institution has only 1 Main Branch
		effect(() => {
			const branches = this.branches();

			if (!this.form.get('institution')?.value) return;

			if (branches.length === 1 && branches[0].isMainInstitution) {
				this.form.get('branchId')?.setValue(branches[0].id, { emitEvent: true });
			}
		});

		// Fetch city districts
		effect(() => {
			this.fetchCityDistricts().then((districts) => {
				this.form.controls.cityDistrictId.enable({ emitEvent: false });
				this.cityDistricts.set(districts);
			});
		});
	}

	public async loadNextInstitutionResults(): Promise<void> {
		if (this.isLoading() || !this.hasMoreInstitutionResults()) return;

		if (!this.institutionSearchTerm.trim()) return;

		this.institutionOffset += this.pageSize;

		await this.updateInstitutionResults(this.institutionSearchTerm, true);
	}

	private async updateInstitutionResults(searchTerm: SearchResultItem | string, append = false): Promise<void> {
		if (typeof searchTerm !== 'string' || !searchTerm.trim()) {
			this.institutionSearchResults.set([]);
			this.totalInstitutionResults.set(0);
			this.hasMoreInstitutionResults.set(false);
			this.institutionOffset = 0;
			return;
		}

		if (!append) {
			this.institutionOffset = 0;
		}

		this.isLoading.set(true);

		try {
			const page = await this.searchApi.search({
				searchInstitutions: true,
				searchInstitutionBranches: false,
				searchClientEnvironments: false,
				searchClients: false,
				searchUsers: false,
				searchInstitutionPerson: false,
				searchSponsors: false,
				searchIso: false,
				searchTerm: searchTerm.trim(),
				offset: this.institutionOffset,
				range: this.pageSize,
			});

			this.totalInstitutionResults.set(page.totalEntities);

			this.hasMoreInstitutionResults.set(this.institutionOffset + page.entities.length < page.totalEntities);

			if (append) {
				this.institutionSearchResults.update((current) => [...current, ...page.entities]);
			} else {
				this.institutionSearchResults.set(page.entities);
			}
		} finally {
			this.isLoading.set(false);
		}
	}

	private updateInstitutionDependents(searchedInstitution: SearchResultItem | string): void {
		const institutionId = (searchedInstitution as SearchResultItem)?.item?.id;
		const previousInstitutionId = this.previousInstitutionId;
		if (institutionId !== undefined && previousInstitutionId !== undefined && institutionId !== previousInstitutionId) {
			this.branches.set([]);
			this.persons.set([]);
			this.form.get('branchId')?.reset(undefined, { emitEvent: false });
			this.form.get('personId')?.reset(undefined, { emitEvent: false });
		}
		this.previousInstitutionId = institutionId;

		if (typeof searchedInstitution === 'string' || institutionId === undefined) {
			this.branches.set([]);
			this.persons.set([]);
			this.form.get('branchId')?.reset(undefined, { emitEvent: false });
			this.form.get('personId')?.reset(undefined, { emitEvent: false });
			this.form.get('branchId')?.disable({ emitEvent: false });
			this.form.get('personId')?.disable({ emitEvent: false });
			return;
		}

		this.fetchBranchesOfInstitution(institutionId).then((branches) => {
			this.form.get('branchId')?.enable({ emitEvent: false });
			const sortedBranches = [...branches].sort((a, b) => Number(b.isMainInstitution) - Number(a.isMainInstitution));
			this.branches.set(sortedBranches);
		});

		this.fetchInstituionPersons(institutionId).then((persons) => {
			this.form.get('personId')?.enable({ emitEvent: false });
			this.persons.set(persons);
		});
	}

	// Sadly we need to use "null" here as "undefined" does not work with ControlValueAccessor
	public writeValue(obj: CreateCareerStatusDynamicFields | null) {
		this.value.set(obj);
		this._onChange(obj);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		this.form.patchValue(obj as any, { emitEvent: false });
	}
	public registerOnChange(fn: unknown) {
		// Sadly we need to use "null" here as "undefined" does not work with ControlValueAccessor
		this._onChange = fn as (_value: CreateCareerStatusDynamicFields | null) => void;
	}
	public registerOnTouched(fn: unknown) {
		this._onTouched = fn as () => void;
	}
	public setDisabledState(isDisabled: boolean) {
		this.disabled.set(isDisabled);
	}

	public hasError = (control: AbstractControl | null, ...errors: string[]) => hasError(control, ...errors);

	public labelAppendAsterisk(label: string) {
		if (this.statusType() === SocialSecurityStatusActivity.J) {
			return `${label}*`;
		}
		return label;
	}

	public getBranchName(id: number) {
		if (!id) return '';
		return this.branches().find((b) => b.id === id)?.name ?? '';
	}

	public institutionName(institution?: SearchResultItem) {
		if (!institution) return '';
		return institution.item?.name ?? `ID: ${institution.item?.id}`; // ID is only shown in an edge case where the institution has no name (which should never happen).
	}

	public async fetchBranchesOfInstitution(institutionId?: number | string) {
		if (!institutionId) return [];

		const page = await this.institutionApi.getBranchesOfInstitution(
			institutionId,
			undefined,
			undefined,
			this.env.pagination.offset,
			this.env.pagination.range,
		);
		return page.entities;
	}

	public async fetchInstituionPersons(institutionId?: number | string) {
		if (!institutionId) return [];

		const page = await this.institutionPersonApi.getAll(
			Number(institutionId),
			this.env.pagination.offset,
			this.env.pagination.range,
		);
		return page.entities;
	}

	public async fetchCityDistricts() {
		const page = await this.categoriesApi.getAllCounties(this.env.pagination.offset, this.env.pagination.range);
		return page.entities;
	}

	// Validator interface implementation
	public validate(_control: AbstractControl): ValidationErrors | null {
		return this.form.valid ? null : { invalidForm: { valid: false, errors: this.form.errors } };
	}

	public registerOnValidatorChange(fn: () => void): void {
		this._onValidationChange = fn;
	}
}
